import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

// Estados posibles: idle, loading, authenticated, unauthenticated, error
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, status: 'loading', error: null };
    case 'AUTHENTICATED':
      return {
        status: 'authenticated',
        user: action.payload.user,
        enrollment: action.payload.enrollment,
        features: action.payload.features,
        error: null
      };
    case 'UNAUTHENTICATED':
      return {
        status: 'unauthenticated',
        user: null,
        enrollment: null,
        features: null,
        error: null
      };
    case 'ERROR':
      return {
        ...state,
        status: 'error',
        error: action.payload
      };
    default:
      return state;
  }
};

const initialState = {
  status: 'idle',
  user: null,
  enrollment: null,
  features: null,
  error: null
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        dispatch({ type: 'UNAUTHENTICATED' });
        return;
      }

      dispatch({ type: 'LOADING' });

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        dispatch({
          type: 'AUTHENTICATED',
          payload: {
            user: response.data.user,
            enrollment: response.data.enrollment,
            features: response.data.features
          }
        });
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        // Token inválido o expirado, limpiar
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        dispatch({ type: 'UNAUTHENTICATED' });
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    dispatch({ type: 'LOADING' });
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Guardar tokens
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      
      // Obtener información del usuario
      const meResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });

      dispatch({
        type: 'AUTHENTICATED',
        payload: {
          user: meResponse.data.user,
          enrollment: meResponse.data.enrollment,
          features: meResponse.data.features
        }
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      dispatch({ type: 'ERROR', payload: message });
      return { success: false, error: message };
    }
  };

  const register = async (email, password, fullName, phone) => {
    dispatch({ type: 'LOADING' });
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName,
        phone
      });
      
      // Guardar tokens
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      
      // Obtener información del usuario
      const meResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });

      dispatch({
        type: 'AUTHENTICATED',
        payload: {
          user: meResponse.data.user,
          enrollment: meResponse.data.enrollment,
          features: meResponse.data.features
        }
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrarse';
      dispatch({ type: 'ERROR', payload: message });
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    dispatch({ type: 'UNAUTHENTICATED' });
  };

  const getToken = () => {
    return localStorage.getItem('access_token');
  };

  const value = {
    ...state,
    isAuthenticated: state.status === 'authenticated',
    isLoading: state.status === 'loading',
    token: getToken(),
    getToken,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
